// @ts-nocheck
/**
 * TOMI NOVO — arrastar a imagem principal da galeria de produto com o MOUSE
 * (desktop) navega entre as fotos de forma fluida, 1:1 com o cursor.
 *
 * Script dedicado e isolado: não mexe no assets/slideshow.js compartilhado
 * pelo resto do tema (hero, cards, outros carrosséis) — só desativa (via
 * atributo data-tomi-custom-drag, ver product-media-gallery-content.liquid)
 * o mousedown nativo do slideshow.js NESSA instância específica, então não
 * há dois handlers competindo pelo mesmo gesto.
 *
 * O "fantasma" de imagem sendo arrastada é evitado por event.preventDefault()
 * no pointerdown (igual ao slideshow.js nativo) + draggable=false na <img> +
 * -webkit-user-drag:none no CSS. O pointer capture só é tomado DEPOIS que um
 * arrasto de verdade é detectado (mesma ordem do slideshow.js nativo) — uma
 * versão anterior capturava o ponteiro imediatamente no pointerdown, o que
 * evitava esse fantasma mas tinha um efeito colateral sério: qualquer clique
 * comum (ex: no botão de zoom) tinha seu evento "click" redirecionado pro
 * elemento que capturou o ponteiro em vez do botão realmente clicado,
 * quebrando o zoom no desktop. Toque (mobile) já rola nativamente, então só
 * tratamos mouse/pen.
 */

const DRAG_THRESHOLD = 4;
const FLICK_VELOCITY = 250;

/**
 * @param {any} component
 */
function bindGalleryDrag(component) {
  if (component.dataset.tomiDragBound) return;
  component.dataset.tomiDragBound = 'true';

  let dragging = false;
  let didDrag = false;
  let startX = 0;
  let startScrollLeft = 0;
  let previousX = 0;
  let previousTime = 0;
  let velocity = 0;
  let pointerId = null;

  component.addEventListener('pointerdown', (event) => {
    if (event.pointerType === 'touch') return;
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('model-viewer, .product-media-container__zoom-button')) return;

    const scroller = component.refs?.scroller;
    const slides = component.refs?.slides;
    if (!scroller || !slides || slides.length <= 1) return;

    dragging = true;
    didDrag = false;
    pointerId = event.pointerId;
    startX = event.clientX;
    startScrollLeft = scroller.scrollLeft;
    previousX = startX;
    previousTime = performance.now();
    velocity = 0;

    scroller.style.scrollSnapType = 'none';
    event.preventDefault();
  });

  component.addEventListener('pointermove', (event) => {
    if (!dragging || event.pointerId !== pointerId) return;

    // TOMI: auto-recuperação — se por algum motivo um pointerup/pointercancel
    // anterior não chegou a resetar o estado (ex: botão solto fora da janela),
    // um pointermove sem o botão principal pressionado encerra o arrasto na
    // hora em vez de deixar "preso" respondendo a simples hover do mouse.
    if (!(event.buttons & 1)) {
      dragging = false;
      return;
    }

    const scroller = component.refs?.scroller;
    if (!scroller) return;

    const delta = event.clientX - startX;

    if (!didDrag && Math.abs(delta) > DRAG_THRESHOLD) {
      didDrag = true;
      component.setAttribute('dragging', '');
      if (typeof component.pause === 'function') component.pause();
      // TOMI: o pointer capture só é tomado aqui, DEPOIS de confirmar que é
      // um arrasto de verdade — ver comentário no topo do arquivo.
      scroller.setPointerCapture(event.pointerId);
    }

    if (!didDrag) return;

    // TOMI: velocidade no MESMO sentido do scrollLeft (arrastar pra esquerda
    // = avança/next), por isso é previousX - clientX e não o contrário —
    // senão o "empurrão" de flick rápido (abaixo) avança pro lado errado.
    const now = performance.now();
    const timeDelta = now - previousTime;
    if (timeDelta > 0) {
      velocity = ((previousX - event.clientX) / timeDelta) * 1000;
    }
    previousX = event.clientX;
    previousTime = now;

    scroller.scrollLeft = startScrollLeft - delta;
    event.preventDefault();
  });

  const endDrag = (event) => {
    if (!dragging || event.pointerId !== pointerId) return;
    dragging = false;

    const scroller = component.refs?.scroller;
    if (scroller) {
      scroller.style.scrollSnapType = '';
      if (scroller.hasPointerCapture?.(event.pointerId)) {
        scroller.releasePointerCapture(event.pointerId);
      }
    }

    component.removeAttribute('dragging');

    if (didDrag) {
      tomiSuppressNextGalleryClick = true;

      const slides = component.refs?.slides;
      if (scroller && slides?.length) {
        const targetIndex = nearestSlideIndex(scroller, slides);
        const current = typeof component.current === 'number' ? component.current : targetIndex;
        const isFastFlick = current === targetIndex && Math.abs(velocity) >= FLICK_VELOCITY;
        const finalIndex = isFastFlick
          ? clampIndex(targetIndex + Math.sign(velocity), slides.length)
          : targetIndex;

        if (typeof component.select === 'function') component.select(finalIndex);
      }

      if (typeof component.resume === 'function') component.resume();
    }
  };

  component.addEventListener('pointerup', endDrag);
  component.addEventListener('pointercancel', endDrag);
}

/**
 * @param {number} value
 * @param {number} length
 */
function clampIndex(value, length) {
  return Math.min(Math.max(value, 0), length - 1);
}

/**
 * @param {HTMLElement} scroller
 * @param {HTMLElement[]} slides
 */
function nearestSlideIndex(scroller, slides) {
  const scrollerRect = scroller.getBoundingClientRect();
  const referenceX = scrollerRect.left + scrollerRect.width / 2;

  let closestIndex = 0;
  let closestDistance = Infinity;

  slides.forEach((slide, index) => {
    if (slide.hasAttribute('hidden')) return;
    const rect = slide.getBoundingClientRect();
    const center = rect.left + rect.width / 2;
    const distance = Math.abs(center - referenceX);

    if (distance < closestDistance) {
      closestDistance = distance;
      closestIndex = index;
    }
  });

  return closestIndex;
}

/** @type {boolean} */
let tomiSuppressNextGalleryClick = false;

// Intercepta o click sintético pós-arrasto ANTES do listener de delegação
// global (document, fase de captura, ver assets/component.js) rodar — a
// fase de captura desce de window -> document -> ... -> alvo, então um
// listener em `window` roda antes de um em `document`.
window.addEventListener(
  'click',
  (event) => {
    if (!tomiSuppressNextGalleryClick) return;
    tomiSuppressNextGalleryClick = false;
    event.stopImmediatePropagation();
    event.preventDefault();
  },
  { capture: true }
);

function scanForGalleryDrag(root = document) {
  root.querySelectorAll('slideshow-component[data-tomi-custom-drag]').forEach((component) => {
    bindGalleryDrag(component);
  });
}

scanForGalleryDrag();

const tomiGalleryDragObserver = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    if (mutation.addedNodes.length) {
      scanForGalleryDrag();
      break;
    }
  }
});

tomiGalleryDragObserver.observe(document.body, { childList: true, subtree: true });
